/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ResponseResult } from '../../apis/response';
import { useEffect } from 'react';
import NavBar from '../../components/NavBar/NavBar';
import Footer2 from '../../components/Footer/Footer2';
import LoadingDiv from '../../components/Loading/LoadingDiv';
import { Font, RoundButton, Select, SocContainer, SocContent, Title } from './style';
import CATION_ICON from '../../assets/images/cation.png';
import { csmsGetApiKey, csmsGetChargingRecord, csmsSetChargingTarget } from '../../stores/actions/csms_action';
import { getPolicy } from '../../stores/actions/policy_action';
import TermsSoc from './components/TermsSoc';

function SocPage() {
    const dispatch = useDispatch();
    const params = useParams();
    const chargerId = params.chargerId;

    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const [socData, setSocData] = useState(null);
    const [selectedSoc, setSelectedSoc] = useState("none");
    const [remainingTime, setRemainingTime] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [csmsToken, setCsmsToken] = useState(null);
    const [isTermsAgreed, setIsTermsAgreed] = useState(false);
    const [termsContent, setTermsContent] = useState('');

    // 충전기 정보 조회
    const getSocData = useCallback((token) => {
        dispatch(csmsGetChargingRecord(chargerId, token)).then(response => {
            setIsInitialLoading(false);
            if (response?.payload?.result === ResponseResult.OK) {
                setSocData(response.payload.data);
                setIsSuccess(true);
            } else {
                setSocData(null);
                setIsSuccess(false);
            }
        }).catch(() => {
            setIsInitialLoading(false);
            setIsSuccess(false);
        });
    }, [dispatch, chargerId]);

    useEffect(() => {
        dispatch(csmsGetApiKey()).then(response => {
            if (response?.payload?.result === ResponseResult.OK) {
                const token = response.payload.data.accessToken;
                setCsmsToken(token);
                getSocData(token);
            } else {
                setIsInitialLoading(false);
                setIsSuccess(false);
            }
        }).catch(() => {
            setIsInitialLoading(false);
            setIsSuccess(false);
        });
        dispatch(getPolicy("(선택) 차량 배터리정보 수집 동의-기후부")).then(response => {
            if (response?.payload?.result === ResponseResult.OK && response.payload.data?.cn) {
                setTermsContent(response.payload.data.cn);
            }
        }).catch(() => { });
    }, [getSocData]);

    // 만료 시간 체크
    useEffect(() => {
        if (socData === null || !socData?.expireDt) return;

        const expireTime = new Date(socData?.expireDt);
        const interval = setInterval(() => {
            const currentTime = new Date();
            const timeDiff = Math.floor((expireTime.getTime() - currentTime.getTime()) / 1000);
            setRemainingTime(timeDiff > 0 ? timeDiff : 0);

            if (currentTime > expireTime) {
                setIsSuccess(false);
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [socData]);

    // 목표 충전량 변경
    const onSubmitHandler = () => {
        if (socData === null) {
            alert("충전기 정보를 찾을 수 없습니다.");
            return;
        }
        if (selectedSoc === "none") {
            alert("충전량을 선택해 주세요.");
            return;
        }
        const reqData = {
            "elctcId": parseInt(socData.elctcId),
            "chrgrIdntfr": socData.chrgrIdntfr,
            "cnnctrId": parseInt(socData.cnnctrId),
            "transactionId": socData.transactionId,
            "requestAt": "QR",
            "soc": parseInt(selectedSoc),
            "smartControlPrivacyYn": isTermsAgreed ? "Y" : "N",
        }
        setIsLoading(true);
        dispatch(csmsSetChargingTarget(reqData, csmsToken)).then(response => {
            if (response?.payload?.result === ResponseResult.OK) {
                alert("목표 충전량이 변경되었습니다.");
                getSocData(csmsToken);
            } else {
                alert("목표 충전량 변경에 실패했습니다.");
            }
        }).catch(() => {
            alert("목표 충전량 변경에 실패했습니다.");
        }).finally(() => {
            setIsLoading(false);
        });
    }

    return (
        <React.Fragment>
            <NavBar />
            {
                isInitialLoading ? <LoadingDiv /> :
                    !isSuccess ? <div css={SocContainer}>
                        <img src={CATION_ICON} alt="" style={{ width: "60px", margin: "24px auto" }} />
                        <div css={Font} style={{ fontSize: "18px", fontWeight: "500", color: "#111111", margin: "0 auto", textAlign: "center" }}>
                            충전기 정보를 찾을 수 없습니다.
                        </div>
                        <div css={Font} style={{ fontSize: "14px", fontWeight: "400", color: "#777777", lineHeight: "1.5", margin: "20px auto", textAlign: "center" }}>
                            충전기가 충전중인 상태가 아니거나<br />
                            QR 코드의 유효 시간이 만료되었습니다.
                        </div>
                    </div> :
                        <div css={SocContainer}>
                            <div css={Title}>목표 충전량 설정</div>
                            <div css={SocContent}>
                                <div css={Font} style={{ fontSize: "14px", fontWeight: "400", color: "#111111" }}>
                                    * 충전량을 선택해 주세요.
                                </div>
                            </div>
                            <div css={SocContent}>
                                <div style={{ minWidth: "125px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <div css={Font} style={{ fontSize: "14px", fontWeight: "400", color: "#111111" }}>
                                        현재 목표 충전량
                                    </div>
                                    <div css={Font} style={{ fontSize: "14px", fontWeight: "400", color: "#111111" }}>
                                        =
                                    </div>
                                </div>
                                <div css={Font} style={{ fontSize: "16px", fontWeight: "500", color: "#111111" }}>
                                    {socData?.targetSoc ? `${socData?.targetSoc}%` : '선택없음'}
                                </div>
                            </div>
                            <div css={SocContent}>
                                <div style={{ minWidth: "125px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <div css={Font} style={{ fontSize: "14px", fontWeight: "400", color: "#111111" }}>
                                        변경할 목표 충전량
                                    </div>
                                    <div css={Font} style={{ fontSize: "14px", fontWeight: "400", color: "#111111" }}>
                                        =
                                    </div>
                                </div>
                                <select css={Select} value={selectedSoc} onChange={(e) => {
                                    setSelectedSoc(e.target.value);
                                }}>
                                    <option value="none" defaultValue={'none'}>선택없음</option>
                                    <option value="100">100%</option>
                                    <option value="95">95%</option>
                                    <option value="90">90%</option>
                                    <option value="85">85%</option>
                                    <option value="80">80%</option>
                                    <option value="75">75%</option>
                                    <option value="70">70%</option>
                                </select>
                                <button css={RoundButton} onClick={() => onSubmitHandler()}>저장</button>
                            </div>
                            <div css={Font} style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                                {
                                    remainingTime > 0 ?
                                        <div style={{ fontSize: "14px", fontWeight: "400", color: "#ff0000" }}>남은 시간 : {remainingTime}초</div> :
                                        null
                                }
                            </div>
                            <TermsSoc
                                termsContent={termsContent}
                                isChecked={isTermsAgreed}
                                onCheckChange={setIsTermsAgreed}
                                title="(선택) 차량 배터리정보 수집 동의-기후부"
                            />
                        </div>
            }
            {
                isLoading ? <LoadingDiv /> : null
            }
            <div css={css`width: 100%; height: 100vh`}>
                <Footer2 />
            </div>
        </React.Fragment>
    )
}

export default SocPage